/** Read-only grid of the bookings in an order. Visible: open, tour, date & time,
 *  supplier #, reseller #, user, tags. custom_id/alt id/state/pax/departure are hidden
 *  by default. Every row opens its booking (the open column, same tab) and each
 *  cell offers the booking value actions. */
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { IconButton, Tooltip } from '@mui/material';
import { DataGridPro, type GridColDef } from '@mui/x-data-grid-pro';
import { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import type { OrderBookingRow } from '@/api/orders';
import { act, utcTitle, ValueActions } from '@/components/ValueActions/ValueActions';
import {
  altCustomIdActions,
  customIdActions,
  departureActions,
  refActions,
  stateActions,
  type BookingCtx,
} from '@/features/bookings/overview/bookingLinks';
import {
  EmptyLine,
  READONLY_GRID_PROPS,
  READONLY_GRID_SX,
  TagsCell,
  TourLink,
  UserLink,
} from '@/features/bookings/overview/common';
import { formatDate } from '@/utils/format';

function buildColumns(ctx: BookingCtx): GridColDef<OrderBookingRow>[] {
  return [
    {
      field: 'open',
      headerName: '',
      width: 44,
      sortable: false,
      renderCell: (p) => (
        <Tooltip title={`Open booking #${p.row.id}`}>
          <IconButton
            size="small"
            component={RouterLink}
            to={`/bookings/${p.row.id}`}
            aria-label={`Open booking #${p.row.id}`}
          >
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
    {
      field: 'tour',
      headerName: 'Tour',
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (p) => <TourLink tour={p.row.tour} ctx={ctx} />,
    },
    {
      field: 'start_ts',
      headerName: 'Date & time',
      width: 180,
      renderCell: (p) => {
        const when = p.row.start_ts ?? p.row.date;
        if (!when) return '—';
        return (
          <ValueActions
            title={p.row.start_ts ? utcTitle(p.row.start_ts) : undefined}
            actions={[act.open(`/bookings/${p.row.id}`, 'Open booking'), ...departureActions(p.row, ctx)]}
          >
            {p.row.start_ts ? formatDate(p.row.start_ts) : when}
          </ValueActions>
        );
      },
    },
    {
      field: 'supplier_ref',
      headerName: 'Supplier #',
      width: 140,
      sortable: false,
      renderCell: (p) =>
        p.row.supplier_ref ? (
          <ValueActions actions={refActions('supplier', p.row.supplier_ref, ctx)}>{p.row.supplier_ref}</ValueActions>
        ) : (
          '—'
        ),
    },
    {
      field: 'reseller_ref',
      headerName: 'Reseller #',
      width: 140,
      sortable: false,
      renderCell: (p) =>
        p.row.reseller_ref ? (
          <ValueActions actions={refActions('reseller', p.row.reseller_ref, ctx)}>{p.row.reseller_ref}</ValueActions>
        ) : (
          '—'
        ),
    },
    {
      field: 'user',
      headerName: 'Guest',
      flex: 1,
      minWidth: 160,
      sortable: false,
      renderCell: (p) => <UserLink user={p.row.user} ctx={ctx} />,
    },
    {
      field: 'tags',
      headerName: 'Tags',
      flex: 1.2,
      minWidth: 180,
      sortable: false,
      renderCell: (p) => <TagsCell tags={p.row.tags} ctx={ctx} />,
    },
    // hidden by default
    {
      field: 'custom_id',
      headerName: 'Custom ID',
      width: 140,
      sortable: false,
      renderCell: (p) =>
        p.row.custom_id ? (
          <ValueActions actions={customIdActions(p.row.custom_id, ctx)}>{p.row.custom_id}</ValueActions>
        ) : (
          '—'
        ),
    },
    {
      field: 'alt_custom_id',
      headerName: 'Alt ID',
      width: 140,
      sortable: false,
      renderCell: (p) =>
        p.row.alt_custom_id ? (
          <ValueActions actions={altCustomIdActions(p.row.alt_custom_id, ctx)}>{p.row.alt_custom_id}</ValueActions>
        ) : (
          '—'
        ),
    },
    {
      field: 'booking_state_display',
      headerName: 'State',
      width: 120,
      renderCell: (p) =>
        p.row.booking_state_display ? (
          <ValueActions actions={stateActions(p.row.booking_state_display, ctx)}>
            {p.row.booking_state_display}
          </ValueActions>
        ) : (
          '—'
        ),
    },
    { field: 'people_count', headerName: 'Pax', width: 70, align: 'right', headerAlign: 'right' },
    {
      field: 'departure',
      headerName: 'Departure',
      width: 120,
      sortable: false,
      renderCell: (p) =>
        p.row.departure?.name ? (
          <ValueActions actions={departureActions(p.row, ctx)}>{p.row.departure.name}</ValueActions>
        ) : (
          '—'
        ),
    },
  ];
}

const HIDDEN_BY_DEFAULT = {
  custom_id: false,
  alt_custom_id: false,
  booking_state_display: false,
  people_count: false,
  departure: false,
};

export function OrderBookingsGrid({ bookings, ctx }: { bookings: OrderBookingRow[]; ctx: BookingCtx }) {
  const columns = useMemo(() => buildColumns(ctx), [ctx]);
  if (!bookings.length) return <EmptyLine>No bookings in this order.</EmptyLine>;
  return (
    <DataGridPro
      rows={bookings}
      columns={columns}
      getRowId={(r) => r.id}
      initialState={{ columns: { columnVisibilityModel: HIDDEN_BY_DEFAULT } }}
      sx={READONLY_GRID_SX}
      {...READONLY_GRID_PROPS}
    />
  );
}
